import { TileSize } from "game-common/models"
import { woodenWallVariant } from "game-common/tile_blocking_geometry"

// Minimal shape needed to z-order a sprited-layer tile.
export interface SpritedTileZIndexItem {
    x: number
    y: number
    context?: { parent?: { x: number; y: number } }
    // Resolved renderer texture IDs use one-based atlas positions.
    tilesetTileId?: string
    belowTilesetTileId?: string
    belowBlocking?: boolean
}

// The cell row a sprited tile should sort by: its structure's base/anchor row (context.parent.y),
// falling back to its own cell when it has no parent (single-tile autotiles).
//
// A wall is two tiles -- a CAP at (x, y-1) and a BASE at (x, y) -- both stamped with
// parent = base cell. Anchoring both to the base row keeps a wall grouped as a unit AND splits
// *stacked* walls in the same contiguous column: the wall to the north sorts by its (higher) base
// row and the wall to the south by its (lower) base row. Previously the whole contiguous run was
// grouped to the run's bottom cell, so a north wall inherited the south wall's row and drew over an
// entity standing between them (occluding the player's head). Multi-tile fragments stamp every tile
// with their anchor origin as parent, so they still group as one unit.
const woodVariant = (id?: string): number => {
    if (!id) return -1
    const separator = id.lastIndexOf("-")
    return woodenWallVariant(id.slice(0, separator), Number(id.slice(separator + 1)) - 1)
}

export const spritedTileAnchorY = (item: SpritedTileZIndexItem): number => {
    // Older corners and static maps can have a cap anchored to its own row. Its
    // artwork and the blocking wooden base directly below establish the actual anchor.
    const parentY = item.context?.parent?.y
    if ((parentY === undefined || parentY === item.y) && woodVariant(item.tilesetTileId) === 13 && item.belowBlocking &&
        woodVariant(item.belowTilesetTileId) >= 0) return item.y + 1
    return parentY ?? item.y
}

// Z-index for a sprited tile anchored at cell row `anchorY`. Larger row (lower on screen) sorts in
// front. `index` is the layer index (small positive int). roughToFineCoordinates(y) === y*TileSize.
export const spritedTileZIndex = (index: number, anchorY: number): number =>
    100 + index + anchorY * TileSize + TileSize / 3
